import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import DateConverter from "../../../utils/DateConverter";
import Contractor from "../../../model/contractor";
import ContractorModal from "./ContractorModal";
import Misc from "../../../utils/Misc";
import useStyles from "./styles";
import { PrimaryColor } from "../../../shared/styles/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "88%",
  },
};

interface Props {
  contractor: Contractor;
  resetList?: () => void;
  onOpen?: (id?: string) => void;
  onClose?: () => void;
}

const ContractorDetailModal = ({
  contractor,
  resetList,
  onOpen,
  onClose,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOpen = () => {
    setOpen(true);
    if (onOpen) {
      onOpen(contractor.id?.toString());
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const contractorId = searchParams.get("contractorId");
    if (!open && contractorId && contractorId === contractor.id?.toString()) {
      handleOpen();
    }
  }, [searchParams]);

  return (
    <>
      <Button onClick={handleOpen}>
        <MoreVertIcon sx={{ color: PrimaryColor}}/>
      </Button>
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <div className={classes.modalHeader}>
              <Typography className={classes.modalTitle}>
                Contractor info
              </Typography>
              <div className={classes.headerSubDiv}>
                <ContractorModal
                  contractor={contractor}
                  resetList={resetList}
                />
                <Btn className={classes.closeButton} onClick={handleClose}>
                  <CloseIcon />
                </Btn>
              </div>
            </div>
            <hr />
            <div className={classes.modalContent}>
              <div className={classes.detailRow}>
                <Typography className={classes.detailLabel}>Name</Typography>
                <Typography className={classes.detailValue}>
                  {contractor.firstName} {contractor.lastName}
                </Typography>
              </div>
              <div className={classes.detailGroup}>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>Email</Typography>
                  <Typography className={classes.detailValue}>
                    {contractor.email}
                  </Typography>
                </div>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>Phone</Typography>
                  <Typography className={classes.detailValue}>
                    {Misc.formatPhoneNumber(contractor.phone)}
                  </Typography>
                </div>
              </div>
              <div className={classes.detailGroup}>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    Company Name
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {contractor.companyName}
                  </Typography>
                </div>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>E.I.N</Typography>
                  <Typography className={classes.detailValue}>
                    {contractor.employeerIdentificationNumber}
                  </Typography>
                </div>
              </div>
              <div className={classes.detailGroup}>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    License Nr
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {contractor.licenseNumber}
                  </Typography>
                </div>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    Insurance Nr
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {contractor.insuranceNumber}
                  </Typography>
                </div>
              </div>
              <div className={classes.detailGroup}>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    Street
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {contractor.street}
                  </Typography>
                </div>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>ZIP</Typography>
                  <Typography className={classes.detailValue}>
                    {contractor.zip}
                  </Typography>
                </div>
              </div>
              <div className={classes.detailGroup}>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>City</Typography>
                  <Typography className={classes.detailValue}>
                    {contractor.city}
                  </Typography>
                </div>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>State</Typography>
                  <Typography className={classes.detailValue}>
                    {contractor.state}
                  </Typography>
                </div>
              </div>
              <div className={classes.detailRow}>
                <Typography className={classes.detailLabel}>
                  Is Verified
                </Typography>
                <Typography className={classes.detailValue}>
                  {contractor.isVerified ? "Yes" : "No"}
                </Typography>
              </div>
              <div className={classes.detailRow}>
                <Typography className={classes.detailLabel}>Notes</Typography>
                <Typography className={classes.detailValue}>
                  {contractor.notes}
                </Typography>
              </div>
              <div className={classes.detailGroup}>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    Created At
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {DateConverter.toFullDateAndHours(contractor.createdAt)}
                  </Typography>
                </div>
                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    Updated At
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {DateConverter.toFullDateAndHours(contractor.updatedAt)}
                  </Typography>
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default ContractorDetailModal;
